import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatErr } from "@/lib/api";
import { INDIAN_STATES } from "@/lib/states";

const Field = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <Label>{label}</Label>
    <Input value={value ?? ""} onChange={e => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} />
  </div>
);

export default function Settings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/config").then(r => setCfg(r.data)).catch(e => toast.error(formatErr(e)));
  }, []);

  const upd = (k, v) => setCfg({ ...cfg, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/config", cfg);
      setCfg(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Company & Bank Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">These details appear on every invoice PDF you generate.</p>
      </div>

      <Card className="p-6 border border-border space-y-4">
        <div className="text-xs uppercase tracking-wider font-semibold text-[hsl(24.6_95%_53.1%)] border-b border-border pb-2">Company Details</div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Company Name" value={cfg.company_name} onChange={v => upd("company_name", v)} testid="cfg-company-name" />
          <Field label="GSTIN" value={cfg.company_gstin} onChange={v => upd("company_gstin", v)} testid="cfg-company-gstin" />
          <div className="col-span-2">
            <Label>Registered Address</Label>
            <Input value={cfg.company_address ?? ""} onChange={e => upd("company_address", e.target.value)} data-testid="cfg-company-address" />
          </div>
          <div>
            <Label>State (for GST)</Label>
            <select value={cfg.company_state ?? ""} onChange={e => upd("company_state", e.target.value)}
              className="w-full h-10 px-3 border border-border rounded-md bg-card text-sm" data-testid="cfg-company-state">
              <option value="">Select State</option>
              {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <Field label="Mobile" value={cfg.company_mobile} onChange={v => upd("company_mobile", v)} testid="cfg-company-mobile" />
          <Field label="Email" value={cfg.company_email} onChange={v => upd("company_email", v)} testid="cfg-company-email" />
        </div>
      </Card>

      <Card className="p-6 border border-border space-y-4">
        <div className="text-xs uppercase tracking-wider font-semibold text-[hsl(24.6_95%_53.1%)] border-b border-border pb-2">🏦 Bank Details (shown on every invoice PDF)</div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Account Holder Name" value={cfg.bank_account_holder} onChange={v => upd("bank_account_holder", v)} testid="cfg-bank-holder" placeholder="e.g. Shree Ram Enterprises" />
          <Field label="Bank Name" value={cfg.bank_name} onChange={v => upd("bank_name", v)} testid="cfg-bank-name" placeholder="e.g. HDFC Bank" />
          <Field label="Account Number" value={cfg.bank_account_no} onChange={v => upd("bank_account_no", v)} testid="cfg-bank-account-no" placeholder="e.g. 50100123456789" />
          <Field label="IFSC Code" value={cfg.bank_ifsc} onChange={v => upd("bank_ifsc", v)} testid="cfg-bank-ifsc" placeholder="e.g. HDFC0001234" />
          <Field label="Branch" value={cfg.bank_branch} onChange={v => upd("bank_branch", v)} testid="cfg-bank-branch" placeholder="e.g. Gurugram Sector 14" />
          <Field label="UPI ID (optional)" value={cfg.upi_id} onChange={v => upd("upi_id", v)} testid="cfg-upi-id" placeholder="e.g. shreeram@hdfc" />
        </div>
      </Card>

      <Card className="p-6 border border-border space-y-4">
        <div className="text-xs uppercase tracking-wider font-semibold text-[hsl(24.6_95%_53.1%)] border-b border-border pb-2">Invoice Numbering</div>
        <div className="grid grid-cols-2 gap-4 items-end">
          <div>
            <Label>Last used Invoice Number (numeric part)</Label>
            <Input type="number" value={cfg.invoice_no_last_used ?? 0} onChange={e => upd("invoice_no_last_used", parseInt(e.target.value) || 0)}
              data-testid="cfg-invoice-last-used" placeholder="e.g. 46" />
            <p className="text-[11px] text-muted-foreground mt-1">Next Tax Invoice will be <b>SRE/{new Date().getFullYear()}/{String((cfg.invoice_no_last_used || 0) + 1).padStart(6, "0")}</b>. Set this once to continue your existing series; the system auto-increments after every sale.</p>
          </div>
          <div>
            <Label>Last used Cash Memo Number (numeric part)</Label>
            <Input type="number" value={cfg.cash_memo_last_used ?? 0} onChange={e => upd("cash_memo_last_used", parseInt(e.target.value) || 0)}
              data-testid="cfg-cash-memo-last-used" placeholder="e.g. 0" />
            <p className="text-[11px] text-muted-foreground mt-1">Next Cash Memo will be <b>SRE/CM/{new Date().getFullYear()}/{String((cfg.cash_memo_last_used || 0) + 1).padStart(5, "0")}</b>. Independent from Tax Invoice series.</p>
          </div>
        </div>
      </Card>

      <Card className="p-6 border border-border space-y-4">
        <div className="text-xs uppercase tracking-wider font-semibold text-[hsl(24.6_95%_53.1%)] border-b border-border pb-2">Invoice Footer</div>
        <div>
          <Label>Terms / Notes shown at bottom of every invoice</Label>
          <textarea value={cfg.invoice_footer_note ?? ""} onChange={e => upd("invoice_footer_note", e.target.value)}
            className="w-full min-h-[80px] px-3 py-2 border border-border rounded-md bg-card text-sm"
            data-testid="cfg-footer-note" />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="cfg-save" className="bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white">
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}
